/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Modal compounds component for Modal component
 *
 * @author Yakov Gushchin
 * @version 1.20.0
 * @since 1.12.0
 */

import { FC, Fragment, ReactNode } from 'react';

import classNames from 'classnames';

import { IAttrStyled } from '../../../types/globalTypes';
import { EModalStatus } from '../types/modalTypes';
import { useModalContext } from './ModalContext';
import {
    ModalBodyStyled,
    ModalPopupControlContainer,
    ModalPopupTextStyled,
    ModalPopupTitleStyled,
} from './styled/ModalStyled';

export type TModalCompositionStyled = FC<IAttrStyled>;

export type TModalCompositionTitleStyled = FC<IAttrStyled & { subTitle?: string }>

export type TModalCompositionControlStyled = FC<IAttrStyled & { center?: boolean }>;

export type TModalCompositionBodyStyled = FC<IAttrStyled & { highlightBg?: boolean; highlightBorder?: boolean }>;

export interface IModalComposition {
    Body: TModalCompositionBodyStyled;
    Control: TModalCompositionControlStyled;
    Status: FC<IModalStatusProps>;
    Text: TModalCompositionStyled;
    Title: TModalCompositionTitleStyled;
}

interface IModalStatusProps {
    children: ReactNode;
    status: EModalStatus;
}

/*
* -------------------------------------------
*           MODAL COMPONENTS
* -------------------------------------------
*/

export const ModalCompoundStatus: FC<IModalStatusProps> = props => {
    const { children, status: componentStatus } = props;
    const { status: currentStatus } = useModalContext();

    // Стартовый компонент не нужно скрывать, а накладывать поверх него лоадер
    const componentIsLoading = currentStatus === EModalStatus.processing && componentStatus === EModalStatus.start;

    return <Fragment>{(currentStatus === componentStatus || componentIsLoading) && children}</Fragment>;
};

/*
* -------------------------------------------
*           MODAL STYLING
* -------------------------------------------
*/

export const ModalCompoundTitle: TModalCompositionTitleStyled = ({ children, className, dataName, subTitle }) => (
    <ModalPopupTitleStyled className={className} data-name={dataName || ''}>
        {children}
        {
            subTitle && (
                <div className="modal-popup-title__subtitle">
                    {subTitle}
                </div>
            )
        }
    </ModalPopupTitleStyled>
);

export const ModalCompoundText: FC<IAttrStyled> = ({ children, className, dataName }) => (
    <ModalPopupTextStyled className={className} data-name={dataName || ''}>{children}</ModalPopupTextStyled>
);

export const ModalCompoundControl: TModalCompositionControlStyled = ({ children, className, center }) => (
    <ModalPopupControlContainer className={classNames(className, { center })}>
        {children}
    </ModalPopupControlContainer>
);

export const ModalBody: TModalCompositionBodyStyled = ({
                                                           children,
                                                           className,
                                                           highlightBg,
                                                           highlightBorder,
                                                           style,
                                                       }) => (
    <ModalBodyStyled
        className={classNames(className, { highlightBg, highlightBorder })}
        style={style}
    >
        {children}
    </ModalBodyStyled>
);
